import React, { Suspense, useEffect, useState } from "react";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { PropTypes } from "prop-types";
import format from "string-template";

//components
import { statusColors } from "../../../utils/colors";
import ChartLegend from "../../../common/ChartLegend";
import Spinner from "../../../common/Spinner";
import AppHOC from "../../../generics/AppHOC";

//reducer files
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import * as actions from "../actions";

//styles
import css from "../applicationsMagneticStyle.less";

//utils
import { createQoeDistributionTooltip } from "../../../utils/views/applications";
import { parseQoeDistrubution } from "../../../config/apiParsers/applications";

import { useI18n } from "../../../i18n/useI18n";

const NoDataAvailable = React.lazy(
  () => import("../../../common/NoDataAvailable"),
);
const ErrorComponent = React.lazy(
  () => import("../../../common/ErrorComponent"),
);
import { downloadAsCSVFile } from "../../../utils/common";
import { EXPORT_CSV_LABELS } from "../../../utils/enums";
const [HbrCard, HbrTooltip, HbrIconButton, HbrButton, HbrIcon, HbrBarChart] =
  reactWrapper([
    "hbr-card",
    "hbr-tooltip",
    "hbr-icon-button",
    "hbr-button",
    "hbr-icon",
    "hbr-bar-chart",
  ]);

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

const QoeDistributionView = ({ reporting = false, ...props }) => {
  const { tViews } = useI18n("views");
  const { globalFilter } = props;
  const [appClassData, setAppClassData] = useState();
  const [maxPoorApps, setMaxPoorApps] = useState();
  const [appClassDataPrev, setAppClassDataPrev] = useState(false);
  const loader = globalFilter.app_data_with_family_sla == undefined;
  const errorAppClass =
    globalFilter?.applicationAggregateData &&
    globalFilter?.applicationAggregateData.resObj &&
    globalFilter?.applicationAggregateData.resObj.message != "successful";

  useEffect(() => {
    setAppClassDataPrev(false);

    if (globalFilter?.app_data_with_family_sla) {
      if (
        globalFilter?.app_data_with_family_sla?.length > 0 &&
        !appClassDataPrev
      ) {
        getData();
      } else if (
        globalFilter.app_data_with_family_sla &&
        globalFilter.app_data_with_family_sla.length == 0 &&
        !appClassDataPrev
      ) {
        setMaxPoorApps([]);
        setAppClassDataPrev(true);
        setAppClassData([]);
        // Notify parent component that data is available for this view for report generation
        props.isReportQoeDistributionCallback?.(true);
      }
    }
  }, [
    globalFilter.selectedSite,
    globalFilter.timePeriodSelected,
    globalFilter.app_data_with_family_sla,
  ]);

  const getData = () => {
    setAppClassData(null);
    setAppClassDataPrev(true);
    let qoeStatusData = [];
    const resData = globalFilter.app_data_with_family_sla;
    if (Array.isArray(resData) && resData.length > 0) {
      const appClassData = {};
      for (const { application_class, vqoe_status } of resData) {
        let item = appClassData[application_class];
        if (item instanceof Object === false)
          item = appClassData[application_class] = {
            application_class,
            bad: 0,
            fair: 0,
            good: 0,
            unknown: 0,
          };
        vqoe_status === "poor" ? (item.bad += 1) : (item[vqoe_status] += 1);
      }
      qoeStatusData = Object.values(appClassData);
    }

    const appClassDataParser = parseQoeDistrubution(qoeStatusData);
    if (reporting) {
      const updatedMaxPoorApps = getPoorestApplicationClass(qoeStatusData);
      setMaxPoorApps(updatedMaxPoorApps);
    }
    setAppClassData(appClassDataParser);
    // Notify parent component that data is available for this view for report generation
    props.isReportQoeDistributionCallback?.(true);
  };

  const getPoorestApplicationClass = (qoeByClass) => {
    if (!qoeByClass.length) return null;
    let maxBad = -1;
    const poorApplicationClasses = qoeByClass.filter(({ bad }) => bad > 0);
    poorApplicationClasses.forEach(({ bad }) => {
      maxBad = bad > maxBad ? bad : maxBad;
    });
    const objPoorApplicationClass = poorApplicationClasses.find(
      ({ bad }) => bad === maxBad,
    );
    return objPoorApplicationClass
      ? objPoorApplicationClass.application_class
      : null;
  };

  /* Function to handle CSV download
    This function is called when the download button is clicked
  */
  const onDownloadCsvClick = () => {
    if (appClassData && appClassData.length > 0) {
      // Define CSV headers
      const headers = [
        "Application",
        "Good",
        "Fair",
        "Poor",
        "Unknown(No QoE)",
      ];
      // Map data rows
      const csvContent = [
        headers,
        ...appClassData.map((item) => [item.date, ...item.values]),
      ]
        .map((row) => row.join(","))
        .join("\n");
      // Create CSV content
      downloadAsCSVFile(
        csvContent,
        EXPORT_CSV_LABELS.APPLICATIONS_QOE_DISTRIBUTION,
      );
    }
  };
  const showCsvDownload =
    !reporting && Array.isArray(appClassData) && appClassData.length > 0;
  return (
    <>
      {reporting && (
        <div className="qoe-distribution-view-title">
          <div className="hbr-type-h2">{tViews("qoeDistributionViewQoE")}</div>
        </div>
      )}
      <HbrCard className="qoe-distribution-view">
        <div
          className={reporting ? "qoe-distributions" : css["qoe-distribution"]}
          data-cy="qoeDistributionView"
        >
          <div className="title-block">
            <div className="flex-items">
              {!reporting && (
                <>
                  <div className="hbr-type-h2">
                    <span
                      className="qoeWordSpacing"
                      role="heading"
                      aria-level={"2"}
                    >
                      {tViews("qoeDistributionViewQoE")}
                    </span>
                  </div>
                  <HbrTooltip
                    content={tViews("qoeDistributionViewTooltip")}
                    placement="top"
                  >
                    <HbrIconButton
                      noPadding
                      size="16px"
                      className="qoeTitleIcon"
                      name="info"
                      sentiment="neutral"
                    />
                  </HbrTooltip>
                  {showCsvDownload && (
                    <div className="qoe-export-csv-download">
                      <HbrTooltip content={tViews("downloadCSV")}>
                        <HbrButton
                          size="medium"
                          variant="text"
                          data-testid="DownloadCsvBtn"
                          onClick={() => onDownloadCsvClick()}
                          arialabel={
                            tViews("a11yLabels.csvDownloadButton") +
                            tViews("a11yLabels.qoeDistribution")
                          }
                        >
                          <HbrIcon
                            name="file-csv"
                            label={tViews("downloadCSV")}
                          />
                        </HbrButton>
                      </HbrTooltip>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {reporting && (
            <p className="title-text">
              {maxPoorApps
                ? format(tViews("qoeDistributionViewSLA"), {
                    object: maxPoorApps,
                  })
                : ""}
            </p>
          )}
          {loader === true ? (
            <div
              className="home-spinner-25"
              data-cy="spinner-componentQoEDistributionView"
            >
              <Spinner />
            </div>
          ) : (
            <div className="qoe-db">
              {loader === false &&
              appClassData &&
              appClassData.length &&
              !errorAppClass ? (
                <div className="content-block">
                  <HbrBarChart
                    config={(() => {
                      const transformedData = appClassData?.map((item) => ({
                        application_class: item.date,
                        Good: item.values[0],
                        Fair: item.values[1],
                        Poor: item.values[2],
                        Unknown: item.values[3],
                      }));

                      // Calculate max value with buffer
                      const maxDataValue =
                        transformedData?.reduce((max, item) => {
                          const total =
                            (item.Good || 0) +
                            (item.Fair || 0) +
                            (item.Poor || 0) +
                            (item.Unknown || 0);
                          return Math.max(max, total);
                        }, 0) || 0;
                      const maxValue = Math.ceil(maxDataValue * 1.2) || 10;

                      return {
                        data: transformedData,
                        keys: ["Good", "Fair", "Poor", "Unknown"],
                        indexBy: "application_class",
                        enableLabel: false,
                        label: () => "",
                        labelSkipWidth: 1000,
                        labelSkipHeight: 1000,
                        height: 220,
                        colors: (bar) => {
                          const colorMap = {
                            Good: statusColors.good,
                            Fair: statusColors.fair,
                            Poor: statusColors.poor,
                            Unknown: statusColors.gray,
                          };
                          return colorMap[bar.id] || statusColors.gray;
                        },
                        margin: { top: 20, right: 20, bottom: 80, left: 60 },
                        padding: 0.2,
                        gridYValues: 5,
                        maxValue: maxValue,
                        minValue: 0,
                        itemTooltip: (node) =>
                          createQoeDistributionTooltip(node, tViews),
                        theme: {
                          grid: {
                            line: {
                              stroke: "#eae9e9ff",
                              strokeWidth: 0.5,
                              strokeDasharray: "0",
                            },
                          },
                          axis: {
                            domain: {
                              line: {
                                stroke: "transparent",
                                strokeWidth: 0,
                              },
                            },
                          },
                        },
                        axisBottom: {
                          tickRotation: -45,
                        },
                        axisLeft: {
                          legend: tViews("qoeDistributionViewAppCount"),
                          legendPosition: "middle",
                          legendOffset: -40,
                          format: (value) =>
                            Number.isInteger(value) ? value : "",
                          tickSize: 0,
                          tickValues: 4,
                        },
                        legends: [],
                      };
                    })()}
                  />
                </div>
              ) : errorAppClass ? (
                <Suspense fallback={<Spinner />}>
                  <ErrorComponent
                    width={"110px"}
                    className={"super-small-dashlet-error"}
                    errorCode={
                      globalFilter.applicationAggregateData.resObj.errorCode
                    }
                    message={
                      globalFilter.applicationAggregateData.resObj.message
                    }
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<Spinner />}>
                  <NoDataAvailable
                    text={tViews("applicationDashboardNoData")}
                  />
                </Suspense>
              )}
            </div>
          )}
          <ChartLegend
            {...props}
            applicationQoe={true}
            includeQuality
            reporting={reporting}
          />
        </div>
      </HbrCard>
    </>
  );
};

QoeDistributionView.propTypes = {
  actions: PropTypes.object.isRequired,
  globalFilter: PropTypes.object.isRequired,
  isReportQoeDistributionCallback: PropTypes.func,
  reporting: PropTypes.bool,
};
export default connect(mapDispatchToProps)(AppHOC(QoeDistributionView));
